import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Clock, Download, XCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  PageSpinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@/components/ui';
import { isApiError } from '@/lib/api/envelope';
import {
  fetchAdminRequests,
  reviewRequest,
  type ClientRequestDto,
  type RequestType,
} from './api';
import { downloadCsv } from '@/lib/utils/csvExport';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  BALANCE_INCREASE: 'Balance Increase',
  EXTEND_EXPIRY: 'Extend Expiry',
  RENEWAL: 'Package Renewal',
  SUPPORT: 'Support',
  PRODUCT_ACCESS: 'Product Access',
};

function statusTone(s: string) {
  if (s === 'PENDING') return 'warning' as const;
  if (s === 'COMPLETED') return 'success' as const;
  if (s === 'REJECTED') return 'danger' as const;
  return 'neutral' as const;
}

function ReviewDialog({
  req,
  onClose,
}: {
  req: ClientRequestDto;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [action, setAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [adminResponse, setAdminResponse] = useState('');
  const [approvedAmount, setApprovedAmount] = useState(
    req.requestedBalanceAmount?.toString() ?? '',
  );
  const [approvedExpiry, setApprovedExpiry] = useState(
    req.requestedExpiryDate?.split('T')[0] ?? '',
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      reviewRequest(req.id, {
        action,
        adminResponse: adminResponse || undefined,
        approvedBalanceAmount: approvedAmount ? parseInt(approvedAmount, 10) : undefined,
        approvedExpiryDate: approvedExpiry ? approvedExpiry + 'T00:00:00Z' : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'client-requests'] });
      qc.invalidateQueries({ queryKey: ['admin', 'pending-count'] });
      onClose();
    },
    onError: (err) => {
      setError(isApiError(err) ? err.firstErrorMessage : 'Failed to submit review.');
    },
  });

  const needsBalance = ['BALANCE_INCREASE', 'RENEWAL'].includes(req.requestType);
  const needsExpiry = ['EXTEND_EXPIRY', 'RENEWAL'].includes(req.requestType);
  const isProductAccess = req.requestType === 'PRODUCT_ACCESS';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface shadow-xl">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-fg">
            Review Request — {REQUEST_TYPE_LABELS[req.requestType] ?? req.requestType}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">
            From {req.clientName ?? req.clientId} · {req.requestedBy}
          </p>
        </div>

        <div className="space-y-4 px-6 py-4 text-sm">
          <div className="rounded-lg bg-surface-subtle p-3 text-xs">
            <p className="font-medium text-fg-muted">Client message</p>
            <p className="mt-1 text-fg">{req.description}</p>
            {isProductAccess && req.requestedProductCode && (
              <p className="mt-2 text-fg-muted">
                Requested product: <span className="font-medium text-fg">{req.requestedProductCode}</span>
              </p>
            )}
            {req.currentBalance != null && (
              <p className="mt-2 text-fg-muted">
                Current balance: <span className="font-medium text-fg">{req.currentBalance.toLocaleString()}</span>
                {req.currentExpiryDate && (
                  <> · Expiry: <span className="font-medium text-fg">{new Date(req.currentExpiryDate).toLocaleDateString()}</span></>
                )}
              </p>
            )}
            {req.requestedBalanceAmount != null && (
              <p className="text-fg-muted">
                Requested: <span className="font-medium text-fg">{req.requestedBalanceAmount.toLocaleString()} credits</span>
              </p>
            )}
            {req.requestedExpiryDate && (
              <p className="text-fg-muted">
                Requested expiry: <span className="font-medium text-fg">{req.requestedExpiryDate.split('T')[0]}</span>
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-xs font-medium text-fg-muted">Action</label>
            <div className="flex gap-2">
              {(['APPROVE', 'REJECT'] as const).map((a) => (
                <button
                  key={a}
                  onClick={() => setAction(a)}
                  className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                    action === a
                      ? a === 'APPROVE'
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                        : 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
                      : 'border-border bg-surface text-fg-muted hover:text-fg'
                  }`}
                >
                  {a === 'APPROVE' ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {a === 'APPROVE' ? 'Approve' : 'Reject'}
                </button>
              ))}
            </div>
          </div>

          {action === 'APPROVE' && needsBalance && (
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">Approved credits</label>
              <input
                type="number"
                min="1"
                value={approvedAmount}
                onChange={(e) => setApprovedAmount(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
              />
            </div>
          )}

          {action === 'APPROVE' && needsExpiry && (
            <div>
              <label className="mb-1 block text-xs font-medium text-fg-muted">Approved expiry date</label>
              <input
                type="date"
                value={approvedExpiry}
                onChange={(e) => setApprovedExpiry(e.target.value)}
                className="h-9 w-full rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
              />
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-fg-muted">
              Admin message to client (optional)
            </label>
            <textarea
              rows={2}
              placeholder="E.g. 'Approved — 10,000 credits added.' or 'Rejected — please contact support.'"
              value={adminResponse}
              onChange={(e) => setAdminResponse(e.target.value)}
              className="w-full resize-none rounded-lg border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary-500"
            />
          </div>

          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-6 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={action === 'REJECT' ? 'danger' : undefined}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {action === 'APPROVE' ? 'Approve & apply' : 'Reject request'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AdminClientRequestsPage() {
  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedReq, setSelectedReq] = useState<ClientRequestDto | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'client-requests', page, filterStatus, filterType],
    queryFn: () =>
      fetchAdminRequests({
        status: filterStatus || undefined,
        requestType: filterType || undefined,
        page,
        size: 20,
      }),
  });

  const requests = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 1;

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportData = await fetchAdminRequests({
        status: filterStatus || undefined,
        requestType: filterType || undefined,
        page: 0,
        size: 5000,
      });
      const headers = ['ID', 'Client', 'Type', 'Requested Product', 'Description', 'Requested Amount', 'Requested Expiry', 'Status', 'Submitted'];
      const csvRows = exportData.content.map((r) => [
        r.id,
        r.clientName ?? r.clientId,
        REQUEST_TYPE_LABELS[r.requestType] ?? r.requestType,
        r.requestedProductCode ?? '',
        r.description,
        r.requestedBalanceAmount ?? '',
        r.requestedExpiryDate?.split('T')[0] ?? '',
        r.status,
        r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      ]);
      downloadCsv(`client-requests-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-fg">Client Requests</h1>
          <p className="mt-0.5 text-sm text-fg-muted">
            Review and action balance, expiry, renewal, and support requests from clients.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={isExporting || requests.length === 0}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <Card>
        <div className="flex flex-wrap gap-3">
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            className="h-9 rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
          >
            <option value="">All statuses</option>
            <option value="PENDING">Pending</option>
            <option value="COMPLETED">Completed</option>
            <option value="REJECTED">Rejected</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
            className="h-9 rounded-lg border border-border bg-surface px-3 text-sm outline-none focus:border-primary-500"
          >
            <option value="">All types</option>
            <option value="BALANCE_INCREASE">Balance Increase</option>
            <option value="EXTEND_EXPIRY">Extend Expiry</option>
            <option value="RENEWAL">Package Renewal</option>
            <option value="SUPPORT">Support</option>
            <option value="PRODUCT_ACCESS">Product Access</option>
          </select>
          {(filterStatus || filterType) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setFilterStatus(''); setFilterType(''); setPage(0); }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {query.isLoading ? (
        <PageSpinner />
      ) : requests.length === 0 ? (
        <EmptyState message="No client requests match your filters." />
      ) : (
        <Card noPadding>
          <Table>
            <THead>
              <TR>
                <TH>Client</TH>
                <TH>Type</TH>
                <TH>Description</TH>
                <TH>Details</TH>
                <TH>Submitted</TH>
                <TH>Status</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {requests.map((req) => (
                <TR key={req.id}>
                  <TD>
                    <div className="font-medium text-fg">{req.clientName ?? req.clientId}</div>
                    <div className="text-[11px] text-fg-muted">{req.requestedBy}</div>
                  </TD>
                  <TD>
                    <Badge tone="primary">
                      {REQUEST_TYPE_LABELS[req.requestType] ?? req.requestType}
                    </Badge>
                  </TD>
                  <TD className="max-w-[240px]">
                    <p className="line-clamp-2 whitespace-normal text-xs text-fg">{req.description}</p>
                  </TD>
                  <TD className="text-xs text-fg-muted">
                    {req.requestedProductCode && (
                      <div className="font-medium text-primary-600 dark:text-primary-400">
                        {req.requestedProductCode}
                      </div>
                    )}
                    {req.requestedBalanceAmount != null && (
                      <div>{req.requestedBalanceAmount.toLocaleString()} credits</div>
                    )}
                    {req.requestedExpiryDate && (
                      <div>{req.requestedExpiryDate.split('T')[0]}</div>
                    )}
                  </TD>
                  <TD className="text-[11px] text-fg-muted">
                    {new Date(req.createdAt).toLocaleString()}
                  </TD>
                  <TD>
                    <div className="flex items-center gap-1.5">
                      {req.status === 'PENDING' ? (
                        <Clock className="h-3.5 w-3.5 text-amber-500" />
                      ) : req.status === 'COMPLETED' ? (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-rose-500" />
                      )}
                      <Badge tone={statusTone(req.status)}>{req.status}</Badge>
                    </div>
                  </TD>
                  <TD>
                    {req.status === 'PENDING' && (
                      <Button size="sm" onClick={() => setSelectedReq(req)}>
                        Review
                      </Button>
                    )}
                    {req.status !== 'PENDING' && req.adminResponse && (
                      <span className="text-[11px] text-fg-subtle" title={req.adminResponse}>
                        {req.adminResponse.length > 30
                          ? req.adminResponse.slice(0, 30) + '…'
                          : req.adminResponse}
                      </span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <span className="text-xs text-fg-muted">Page {page + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {selectedReq && (
        <ReviewDialog req={selectedReq} onClose={() => setSelectedReq(null)} />
      )}
    </div>
  );
}
